import { useCallback, useRef, useState } from "react";
import { QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "react-query";

type UsePollingQueryOptions<TData, TError> = UseQueryOptions<TData, TError> & {
  queryKey: QueryKey;
  queryFn: () => Promise<TData>;
  interval?: number;
  initiallyEnabled?: boolean;
  maxTriesOnFailure?: number;
  maxTries?: number;
};

type UsePollingQueryResult<TData, TError> = UseQueryResult<TData, TError> & {
  startPolling: () => void;
  stopPolling: () => void;
  isPolling: boolean;
};

const usePollingQuery = <TData = unknown, TError = unknown>({
  queryKey,
  queryFn,
  interval = 5000,
  initiallyEnabled = false,
  maxTriesOnFailure = Infinity, // Default to no limit
  maxTries = Infinity,
  ...rest
}: UsePollingQueryOptions<TData, TError>): UsePollingQueryResult<TData, TError> => {
  const [enabled, setEnabled] = useState(initiallyEnabled);
  const failureCount = useRef(1);
  const fetchCount = useRef(1);
  const startPolling = useCallback(() => {
    setEnabled(true);
  }, []);
  const stopPolling = useCallback(() => {
    setEnabled(false);
  }, []);
  const query = useQuery<TData, TError>({
    queryKey,
    queryFn: async () => {
      try {
        const data = await queryFn();

        fetchCount.current += 1;

        if (fetchCount.current > maxTries) {
          setEnabled(false);
          fetchCount.current = 1; // reset on success
        }

        failureCount.current = 1; // reset on success
        return data;
      } catch (err) {
        failureCount.current += 1;
        if (failureCount.current >= maxTriesOnFailure) {
          setEnabled(false); // stop polling
        }
        throw err;
      }
    },
    enabled,
    refetchInterval: enabled ? interval : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: false, // we handle retry logic manually
    ...rest,
  });
  return {
    ...query,
    startPolling,
    stopPolling,
    isPolling: enabled,
  };
};
export default usePollingQuery;
